import { GameWorld } from "./GameWorld.tsx";
import { useGameAudio } from "./gameAudio.ts";
import { useEffect, useMemo, useRef, useState } from "react";
import { BOAT_PATH, pointAlongPath } from "../shared/game.ts";
import { useLocale } from "./i18n.tsx";
import { GentleGameArena, GentleGameIntro, GentleGameResult } from "./GentleGameFrame.tsx";
import type { Session } from "./Shell.tsx";
import { useGentleGame } from "./useGentleGame.ts";

const TEXT = {
  "zh-CN": { eyebrow: "连续平滑", title: "纸船归港", lead: "轻轻转头驾驶纸船，沿着水纹经过八个航标，回到木码头。水流会停下来等你，没有失败。", preview: "一只纸船沿柔和水流前进", follow: "把纸船驶进前方的水纹", return: "水流在等你，把船带回水纹", detail: (progress: number, elapsed: number) => `航程 ${progress}% · ${elapsed} 秒`, completeEyebrow: "顺利靠岸", completeTitle: "纸船回家了", completeBody: (seconds: number) => `你用 ${seconds} 秒完成了一段平稳航程。` },
  en: { eyebrow: "Smooth and continuous", title: "Paper Boat Home", lead: "Steer your paper boat with gentle head movements. Pass eight river markers and sail home to the wooden dock. The water waits if you drift away.", preview: "A paper boat following a gentle stream", follow: "Sail into the ripples ahead", return: "The water is waiting. Bring your boat to the ripples", detail: (progress: number, elapsed: number) => `${progress}% of the journey · ${elapsed}s`, completeEyebrow: "Safely ashore", completeTitle: "The boat is home", completeBody: (seconds: number) => `You completed a calm, steady journey in ${seconds} seconds.` },
} as const;

export function BoatGamePage({ session }: { session: Session | null }) {
  const { locale } = useLocale();
  const text = TEXT[locale];
  const runtime = useGentleGame("paper_boat", session);
  const audio = useGameAudio("boat");
  const soundStep = useRef(0);
  const playableRuntime = { ...runtime, startCamera: async () => { audio.unlock(); await runtime.startCamera(); }, startManual: () => { audio.unlock(); runtime.startManual(); }, reset: () => { audio.stop(); runtime.reset(); } };
  const pointRef = useRef(runtime.point);
  pointRef.current = runtime.point;
  const progressRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [onCourse, setOnCourse] = useState(true);
  const guide = useMemo(() => pointAlongPath(BOAT_PATH, progress), [progress]);

  useEffect(() => {
    if (runtime.stage === "intro") { progressRef.current = 0; setProgress(0); setOnCourse(true); soundStep.current = 0; }
  }, [runtime.stage]);

  useEffect(() => {
    if (runtime.stage !== "playing") return;
    audio.play("start");
    let animation = 0;
    let last = performance.now();
    let lastPainted = -1;
    let lastCourse = true;
    const tick = (now: number) => {
      const delta = Math.min(100, now - last);
      last = now;
      const target = pointAlongPath(BOAT_PATH, progressRef.current);
      const point = pointRef.current;
      const close = Boolean(point && Math.hypot(point.x - target.x, point.y - target.y) <= 0.3);
      if (close) progressRef.current = Math.min(1, progressRef.current + delta / 42_000);
      if (close !== lastCourse) { lastCourse = close; setOnCourse(close); }
      if (progressRef.current - lastPainted >= 0.002 || progressRef.current === 1) { lastPainted = progressRef.current; setProgress(progressRef.current); }
      const checkpoint = Math.min(8, Math.floor(progressRef.current * (BOAT_PATH.length - 1)));
      if (checkpoint > soundStep.current) { audio.play("arrive"); soundStep.current = checkpoint; }
      if (progressRef.current >= 1) { audio.play("complete"); runtime.complete(); return; }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animation);
  }, [audio.play, runtime.complete, runtime.stage]);

  const preview = <GameWorld game="boat" progress={.36} cursor={pointAlongPath(BOAT_PATH, .36)} />;
  if (runtime.stage === "intro") return <GentleGameIntro runtime={playableRuntime} eyebrow={text.eyebrow} title={text.title} lead={text.lead} preview={preview} previewLabel={text.preview} tone="boat" />;
  if (runtime.stage === "complete") return <GentleGameResult runtime={playableRuntime} eyebrow={text.completeEyebrow} title={text.completeTitle} body={text.completeBody(runtime.elapsed)} art={<GameWorld game="boat" progress={1} />} tone="boat" />;
  return <GentleGameArena runtime={playableRuntime} progress={progress} arenaLabel={text.preview} playingTitle={onCourse ? text.follow : text.return} playingDetail={text.detail(Math.round(progress * 100), runtime.elapsed)} className="boat-game" controls={{ soundEnabled: audio.enabled, toggleSound: audio.toggle }}>
    <GameWorld game="boat" cursor={runtime.cursor} guide={guide} progress={progress} onCourse={onCourse} active={runtime.stage === "playing"} />
  </GentleGameArena>;
}
